import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MedicalComponentSearchDialogComponent } from 'guideline/feature-component-search';
import { MedicationRequestService } from 'shared/domain';
import { ConfirmationDialogComponent, DialogService, IconDirective, ButtonDirective } from 'shared/ui-common';
import { isTruthy } from 'shared/util-common';

import { GuidelineActionDto, FlowchartElementType } from '@treetop/shared/model';

import { MedicationEditDialogComponent } from '../edit-dialog/medication-edit-dialog.component';
import { MedicationCardComponent } from '../medication-card/medication-card.component';

@Component({
  standalone: true,
  imports: [MatButtonModule, TranslocoModule, MatIconModule, IconDirective, RouterLink, MedicationCardComponent, ButtonDirective],
  selector: 'sidebar-medications',
  templateUrl: './sidebar-medications.component.html',
  styleUrls: ['./sidebar-medications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMedicationsComponent {
  protected medicationRequestService = inject(MedicationRequestService);
  private dialogService = inject(DialogService);
  public dialog = inject(MatDialog);

  public isMedicationTab = input<boolean>();

  protected guidelineComponent = input.required<GuidelineActionDto>();

  @Input() public elementType: FlowchartElementType;

  @Output() public showAllMedicationsToggle = new EventEmitter();
  @Output() public medicationsChanged = new EventEmitter<GuidelineActionDto[]>();

  protected medicationRequestsCount = computed(() => this.guidelineComponent().medicationRequests.length);
  protected showAll = false;
  private component = new FormControl();

  protected showAllMedications(): void {
    this.showAll = !this.showAll;
    this.showAllMedicationsToggle.emit(this.showAll);
  }

  protected openAddMedicationDialog(): void {
    const searchDialog = this.dialogService.open(MedicalComponentSearchDialogComponent, {
      title: 'AddMedication',
      data: {
        definition: this.component,
        findingFilterActive: false,
        activityFilterActive: false,
        observationFilterActive: false,
        enableMultiSearch: false,
        isFilterSearch: true,
        elementType: this.elementType,
      },
      disableClose: false,
    });

    searchDialog
      .afterClosed()
      .pipe(isTruthy())
      .subscribe((result) => {
        const newMedicationRequests = this.convertMedications(result);
        this.medicationsChanged.emit(newMedicationRequests);
      });
  }

  protected convertMedications(medication) {
    const medicationsToConvert = [medication.value];
    const medicationRequestsToAdd = this.medicationRequestService.convertActivityToMedicationRequest(medicationsToConvert);

    return [...medicationRequestsToAdd.value, ...this.guidelineComponent().medicationRequests];
  }

  protected deleteMedication(id: string): void {
    this.confirmationDialog()
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        const newMedications = this.guidelineComponent().medicationRequests.filter((medicationRequest) => medicationRequest.id !== id);
        this.medicationsChanged.emit(newMedications);
      });
  }

  protected confirmationDialog() {
    return this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        icon: 'triangle-alert',
        title: 'Sidebar.DeleteMedicationDialog.Title',
        message: 'Sidebar.DeleteMedicationDialog.Message',
        cancelButtonCaption: 'Cancel',
        confirmButtonCaption: 'Delete',
      },
      disableClose: true,
    });
  }

  protected openEditDialog(medication) {
    const dialogRef = this.dialog.open(MedicationEditDialogComponent, {
      data: {
        title: medication.title,
        name: medication.title,
        description: medication.description !== null ? this.convertHTMLToLineBreaks(medication.description) : medication.description,
      },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const newMedicationRequests = this.guidelineComponent().medicationRequests.map((request) =>
          request.id === medication.id
            ? { ...request, title: result.title, description: this.convertLineBreaksToHTML(result.description) }
            : request,
        );

        this.medicationsChanged.emit(newMedicationRequests);
      }
    });
  }

  private convertLineBreaksToHTML(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  private convertHTMLToLineBreaks(text: string): string {
    return text.replace(/<br>/g, '\n');
  }
}
